'use client';

import { ReactTable, type RowData } from '@/lib/tanstack-table';
import { useSelector } from '@tanstack/react-store';
import { XIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface DataTableToolbarProps<TData extends RowData> {
  table: ReactTable<TData>;
  facetedFilters?: React.ReactNode;
  search?: DataTableSearchProps;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  children?: React.ReactNode;
  /** Right-aligned items, e.g. sync status and the page's primary action. */
  actions?: React.ReactNode;
  onClearFilters?: () => void;
  hasCustomFilters?: boolean;
}

interface DataTableSearchProps {
  placeholder: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// fallow-ignore-next-line complexity
function DataTableToolbarSearch({
  search,
  searchPlaceholder,
  searchValue,
  onSearchChange,
}: Pick<
  DataTableToolbarProps<RowData>,
  'search' | 'searchPlaceholder' | 'searchValue' | 'onSearchChange'
>) {
  if (!search && !searchPlaceholder) return null;
  return (
    <Input
      placeholder={search?.placeholder || searchPlaceholder || 'Search...'}
      value={search?.value || searchValue || ''}
      onChange={search?.onChange || ((e) => onSearchChange?.(e.target.value))}
      className="h-8 w-[150px] lg:w-[250px]"
    />
  );
}

/**
 * @example
 * <DataTableToolbar table={table} filters={[
 *    <DataTableFacetedFilter
 *        column={table.getColumn('status')}
 *        title="Status"
 *        options={statuses}
 *    />,
 *    <DataTableFacetedFilter
 *        column={table.getColumn('priority')}
 *        title="Priority"
 *        options={priorities}
 *    />,
 * ]} />
 */
export function DataTableToolbar<TData extends RowData>({
  table,
  facetedFilters,
  search,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  children,
  actions,
  onClearFilters,
  hasCustomFilters,
}: DataTableToolbarProps<TData>) {
  const columnFilters = useSelector(table.atoms.columnFilters);
  const globalFilter = useSelector(table.atoms.globalFilter);
  const hasColumnFilters = columnFilters.length > 0;
  const hasGlobalFilter = globalFilter;
  const isFiltered = hasColumnFilters || hasGlobalFilter || hasCustomFilters;

  const handleClearFilters = () => {
    table.resetColumnFilters();
    table.setGlobalFilter('');
    onClearFilters?.();
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center gap-2 flex-wrap">
        <DataTableToolbarSearch
          search={search}
          searchPlaceholder={searchPlaceholder}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
        />
        {facetedFilters}
        {children}
        {isFiltered && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <T context="button label to clear all active table filters">
              Clear filters
            </T>
            <XIcon />
          </Button>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
